import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Edit } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/components/ui/Dialog';
import { Button } from '@/shared/components/ui/Button';
import { TaskForm } from './TaskForm';
import { useUpdateTask } from '@/shared/hooks';
import { useSignalsByIds } from '@/shared/hooks/useSignals';
import {
  IssueStatus,
  TaskIssue,
  type RelatedSignalsApiResponse,
} from '@momentum/types';
import {
  recurrenceEndConfigSchema,
  type CreateTaskFormData,
} from './types/tasks.types';
import { buildChangedFields } from '@/shared/utils/form-change-utils';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';
import z from 'zod';

interface UpdateTaskModalProps {
  task: TaskIssue;
}

export const UpdateTaskModal: React.FC<UpdateTaskModalProps> = ({ task }) => {
  const { t } = useTranslation('goals');
  const [open, setOpen] = useState(false);
  const updateTaskMutation = useUpdateTask();
  const currentEmployee = useEmployeeContext();

  const taskSignalIds =
    task.relatedSignals?.map((l) => l.signalId).filter(Boolean) ?? [];
  const { data: taskSignals = [] } = useSignalsByIds(taskSignalIds);

  const contextualTitleBySignalId = useMemo(
    () =>
      new Map(
        (task.relatedSignals ?? [])
          .filter((entry) => entry.contextualTitle)
          .map((entry) => [entry.signalId, entry.contextualTitle])
      ),
    [task.relatedSignals]
  );

  const initialApprovedSignals = useMemo<RelatedSignalsApiResponse[]>(
    () =>
      taskSignals.map((signal) => ({
        id: signal._id,
        signal: signal.title,
        contextualTitle: contextualTitleBySignalId.get(signal._id),
      })),
    [taskSignals, contextualTitleBySignalId]
  );

  const initialValues: CreateTaskFormData = {
    title: task.title,
    description: task.description || '',
    assignees: task.assignees.map((assignee) => assignee),
    link: task.link,
    collaborators: task.collaborators || [],
    startDate: task.startDate,
    dueDate: task.dueDate,
    status: (task.status as IssueStatus) || IssueStatus.TODO,
    parent: task.parent || '',
    recurrence: task.recurrence
      ? {
          config: task.recurrence.config,
          startDate: task.recurrence.startDate,
          endConfig: task.recurrence.endConfig as z.infer<
            typeof recurrenceEndConfigSchema
          >,
        }
      : undefined,
  };

  const isRecurringTask = !!task.recurrence;

  const handleSubmit = async (
    formData: CreateTaskFormData,
    signals: RelatedSignalsApiResponse[]
  ) => {
    // Build relatedSignals with full objects
    const existingSignalsMap = new Map(
      (task.relatedSignals ?? []).map((signal) => [signal.signalId, signal])
    );

    const newRelatedSignals = signals.map((signal) => {
      const existing = existingSignalsMap.get(signal.id);
      if (existing) {
        // Keep original metadata for existing signals
        return existing;
      }
      // New signal - add with fresh metadata
      return {
        signalId: signal.id,
        addedAt: new Date().toISOString(),
        addedBy: currentEmployee._id,
        ...(signal.contextualTitle
          ? { contextualTitle: signal.contextualTitle }
          : {}),
      };
    });

    const currentRelatedSignals = task.relatedSignals || [];

    // Only send fields that have changed (PATCH, not PUT)
    const changedFields = buildChangedFields([
      { key: 'title', newValue: formData.title, oldValue: initialValues.title },
      {
        key: 'description',
        newValue: formData.description,
        oldValue: initialValues.description,
      },
      {
        key: 'assignees',
        newValue: formData.assignees,
        oldValue: initialValues.assignees,
        deepCompare: true,
      },
      {
        key: 'status',
        newValue: formData.status,
        oldValue: initialValues.status,
      },
      {
        key: 'startDate',
        newValue: formData.startDate,
        oldValue: initialValues.startDate,
      },
      {
        key: 'dueDate',
        newValue: formData.dueDate,
        oldValue: initialValues.dueDate,
      },
      {
        key: 'parent',
        newValue: formData.parent,
        oldValue: initialValues.parent,
      },
      { key: 'link', newValue: formData.link, oldValue: initialValues.link },
      {
        key: 'collaborators',
        newValue: formData.collaborators,
        oldValue: initialValues.collaborators,
        deepCompare: true,
      },
      {
        key: 'relatedSignals',
        newValue: newRelatedSignals,
        oldValue: currentRelatedSignals,
        deepCompare: true,
      },
      {
        key: 'recurrence',
        newValue: formData.recurrence,
        oldValue: initialValues.recurrence,
        deepCompare: true,
      },
    ]);

    // Only send the request if there are changes
    if (Object.keys(changedFields).length > 0) {
      await updateTaskMutation.mutateAsync({
        id: task._id,
        data: changedFields,
      });
    }

    // Close modal on success
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100"
        >
          <Edit className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent
        size="lg"
        className="flex flex-col max-h-[90vh] p-0"
        dismissibleByOutsideClick={false}
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border shrink-0">
          <DialogTitle className="text-xl font-bold">
            {t('updateTaskTitle')}
          </DialogTitle>
          {isRecurringTask && (
            <p className="text-sm text-muted-foreground mt-2">
              {t('editingSingleOccurrence')}
            </p>
          )}
        </DialogHeader>
        <div className="flex-1 min-h-0 px-6 py-4">
          <TaskForm
            initialValues={initialValues}
            isPending={updateTaskMutation.isPending}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            submitLabel={t('updateTask')}
            submittingLabel={t('updating')}
            initialApprovedSignals={initialApprovedSignals}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
